import {EventEmitter, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {CiOrderedMove, CiOrderedRemoval} from '../../../../../profiler-theme';
import {MessageService} from '../../../../shared/components/message.service';
import {Requests} from '../../../../shared/model/request.model';
import {VocabularyService} from '../../../../services/vocabulary.service';
import {vocabularyName} from '../../../../shared/model/vocabulary-name';

export abstract class DropdownsBase<T extends { id: string; name?: string; nameEn?: string }> {
  /** Angezeigt und sortiert in der Oberflächensprache; gespeichert wird weiterhin der deutsche Name. */
  items: T[] = [];
  selectedNames: string[] = [];

  selectedIds: string[] = [];

  protected idByName = new Map<string, string>();
  protected nameById = new Map<string, string>();
  private displayNameById = new Map<string, string>();
  private source: T[] = [];
  private readonly vocabulary = inject(VocabularyService);

  readonly change = new EventEmitter<string[]>();

  abstract readonly maxItems: number;

  protected constructor(
    protected translate: TranslateService,
    protected liveAnnouncer: LiveAnnouncer,
    protected messageService: MessageService
  ) {
    this.vocabulary.changes$.pipe(takeUntilDestroyed()).subscribe(() => this.localize());
  }

  protected init(items: T[], selectedNames: string[]) {
    this.source = items ?? [];
    this.localize();
    this.selectedNames = selectedNames ?? [];

    this.idByName = new Map(this.source.map(i => [i.name, i.id]));
    this.nameById = new Map(this.source.map(i => [i.id, i.name]));

    this.selectedIds = this.selectedNames
      .map(name => this.idByName.get(name))
      .filter((id): id is string => !!id);
  }

  private localize() {
    const language = this.vocabulary.language;
    this.items = this.vocabulary.sort(
      this.source.map(item => ({...item, name: vocabularyName(item, language)})),
      item => item.name ?? '',
    );
    this.displayNameById = new Map(this.items.map(i => [i.id, i.name ?? '']));
  }

  onSelectedIdsChange(ids: string[]) {
    this.selectedIds = [...ids];
    this.emit();
  }

  /** Der Dublettentreffer ist schon freigegeben und wird statt einer neuen Anfrage ausgewählt. */
  onExistingChosen(existing: Requests) {
    if (!this.nameById.has(existing.id)) {
      // Die Auswahlliste wird einmal pro Seitenaufruf geladen; eine spätere Freigabe fehlt darin.
      this.messageService.warning('request.duplicate.reloadNeeded');
      return;
    }
    if (this.selectedIds.includes(existing.id)) {
      this.messageService.info('request.duplicate.alreadySelected');
      return;
    }
    if (this.selectedIds.length >= this.maxItems) {
      this.onMaxExceeded(existing.id);
      return;
    }
    this.onSelectedIdsChange([...this.selectedIds, existing.id]);
    this.onItemsAdded();
  }

  onItemsAdded() {
    this.announce('personalData.aria.added', {total: this.selectedIds.length});
  }

  onItemsRemoved(ids: string[]) {
    for (const id of ids) {
      this.announce('personalData.aria.deleted', {item: this.nameFor(id)});
    }
  }

  onItemRemoved(removal: CiOrderedRemoval) {
    this.announce('personalData.aria.deleted', {item: this.nameFor(removal.id)});
  }

  onItemMoved(move: CiOrderedMove) {
    this.announce('personalData.aria.moved', {
      item: this.nameFor(move.id),
      from: move.from,
      to: move.to,
      total: move.total
    });
  }

  onMaxExceeded(id: string) {
    const message = this.translate.instant('personalData.aria.maxLength', {item: this.nameFor(id)});
    this.messageService.warning(message);
    this.liveAnnouncer.announce(message);
  }

  readonly moveUpAriaLabel = (item: string, position: number, total: number): string =>
    this.translate.instant('personalData.aria.moveUpItem', {item, from: position, to: position - 1, total});

  readonly moveDownAriaLabel = (item: string, position: number, total: number): string =>
    this.translate.instant('personalData.aria.moveDownItem', {item, from: position, to: position + 1, total});

  readonly deleteAriaLabel = (item: string, position: number, total: number): string =>
    this.translate.instant('personalData.aria.deleteItem', {item, position, total});

  protected emit() {
    const names = this.selectedIds
      .map(id => this.nameById.get(id))
      .filter((n): n is string => !!n);

    this.change.emit(names);
  }

  protected nameFor(id: string): string {
    return this.displayNameById.get(id) || this.nameById.get(id) || id;
  }

  protected announce(key: string, params: Record<string, unknown>) {
    this.liveAnnouncer.announce(
      this.translate.instant(key, params)
    );
  }
}
